(function () {
  'use strict';

  /*
   * TAGHILLS LENS CONFIGURATOR
   * File: assets/lens-configurator.js
   *
   * Features:
   * - Three-step lens configurator
   * - Prescription photo upload from camera or gallery
   * - JPEG compression before upload
   * - Google Drive image storage through Apps Script
   * - Prescription records saved to Google Sheets
   * - Frame + Lens added to Shopify cart with matching configuration ID
   */

  const TAGHILLS_PRESCRIPTION_API =
    'https://script.google.com/macros/s/AKfycbx4Sowny1C8-Uiv4ru6Sul12-jbhxKg9iYF2PrKaMEAzfO_LLdfU5b2qceQx0n7H2xIKA/exec';

  class TAGHillsLensConfigurator {
    constructor() {
      this.root = document.getElementById(
        'TAGHillsLensConfigurator'
      );

      if (!this.root) {
        console.error(
          '[TAGHills] Configurator root not found.'
        );
        return;
      }

      // Move the modal to body to prevent clipping by parent containers.
      if (this.root.parentElement !== document.body) {
        document.body.appendChild(this.root);
      }

      this.body = this.root.querySelector('.th-lc__body');

      this.steps = Array.from(
        this.root.querySelectorAll('[data-step]')
      );

      this.progress = Array.from(
        this.root.querySelectorAll('[data-progress]')
      );

      this.nextButton = this.root.querySelector(
        '[data-lens-next]'
      );

      this.backButton = this.root.querySelector(
        '[data-lens-back]'
      );

      this.totalElement = this.root.querySelector(
        '[data-lens-total]'
      );

      this.lensList = this.root.querySelector(
        '[data-lens-products]'
      );

      this.prescriptionDetails = this.root.querySelector(
        '[data-prescription-details]'
      );

      this.currentStep = 1;
      this.isSubmitting = false;
      this.configuration = this.createEmptyConfiguration();

      this.bindEvents();
      this.updateUI();

      console.log(
        '[TAGHills] Lens configurator initialized.'
      );
    }

    /*
     * CONFIGURATION
     */

    createEmptyConfiguration() {
      return {
        id: null,

        frame: {
          productId: null,
          variantId: null,
          title: null,
          price: 0
        },

        lens: {
          type: null,
          productId: null,
          variantId: null,
          title: null,
          price: 0
        },

        prescription: {
          method: null,
          name: '',
          driveUrl: '',
          imageDataUrl: '',
          imageName: '',

          manual: {
            right: {
              sph: '',
              cyl: '',
              axis: ''
            },
            left: {
              sph: '',
              cyl: '',
              axis: ''
            },
            pd: ''
          }
        }
      };
    }

    /*
     * EVENT HANDLERS
     */

    bindEvents() {
      // Open configurator from product page buttons.
      document.addEventListener('click', (event) => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        const button = target.closest(
          '[data-open-lens-configurator]'
        );

        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        this.openFromButton(button);
      });

      // Prescription photo selection.
      // Delegation supports the dynamically rendered input.
      this.root.addEventListener('change', (event) => {
        const target = event.target;

        if (!(target instanceof HTMLInputElement)) return;

        if (!target.matches('[data-prescription-image]')) {
          return;
        }

        const file = target.files && target.files[0];

        if (file) {
          this.handlePrescriptionImage(file);
        }
      });

      // Configurator controls.
      this.root.addEventListener('click', (event) => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        const closeButton = target.closest(
          '[data-lens-configurator-close]'
        );

        if (closeButton) {
          event.preventDefault();
          this.close();
          return;
        }

        const typeButton = target.closest(
          '[data-lens-type]'
        );

        if (
          typeButton &&
          this.root.contains(typeButton)
        ) {
          event.preventDefault();

          this.selectLensType(
            typeButton.dataset.lensType
          );

          return;
        }

        const lensCard = target.closest(
          '[data-lens-product]'
        );

        if (
          lensCard &&
          this.root.contains(lensCard)
        ) {
          event.preventDefault();

          this.selectLens(
            lensCard.dataset.productId
          );

          return;
        }

        const prescriptionButton = target.closest(
          '[data-prescription-method]'
        );

        if (
          prescriptionButton &&
          this.root.contains(prescriptionButton)
        ) {
          event.preventDefault();

          this.selectPrescriptionMethod(
            prescriptionButton.dataset.prescriptionMethod
          );
        }
      });

      if (this.nextButton) {
        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.next();
        });
      }

      if (this.backButton) {
        this.backButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.back();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (
          event.key === 'Escape' &&
          this.root.classList.contains('is-open')
        ) {
          this.close();
        }
      });
    }

    /*
     * OPEN CONFIGURATOR
     */

    openFromButton(button) {
      const form = button.closest('form');

      let variantId =
        button.dataset.frameVariantId || '';

      // Prefer the currently selected product variant.
      if (form) {
        const variantInput = form.querySelector(
          'input[name="id"]'
        );

        if (variantInput && variantInput.value) {
          variantId = variantInput.value;
        }
      }

      const productId = button.dataset.productId || '';

      const title = button.dataset.productTitle || '';

      const framePrice = Number(
        button.dataset.framePrice || 0
      );

      if (!productId || !variantId) {
        console.error('[TAGHills] Frame data missing:', {
          productId,
          variantId,
          button
        });

        alert(
          'Unable to identify this frame. Please select a frame variant and try again.'
        );

        return;
      }

      this.open({
        productId,
        variantId,
        title,
        price: framePrice
      });
    }

    open(frameData) {
      if (!frameData || !frameData.productId || !frameData.variantId) {
        alert('Missing frame data. Please select your frame again.');
        return;
      }

      this.configuration = this.createEmptyConfiguration();

      this.configuration.id = this.createConfigurationId();

      this.configuration.frame = {
        productId: frameData.productId,
        variantId: String(frameData.variantId),
        title: frameData.title || '',
        price: Number(frameData.price || 0)
      };

      this.currentStep = 1;
      this.isSubmitting = false;

      this.clearSelections();

      if (this.prescriptionDetails) {
        this.prescriptionDetails.innerHTML = '';
        this.prescriptionDetails.hidden = true;
      }

      this.renderLensProducts();
      this.updateUI();

      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');

      document.body.classList.add('th-lc-open');

      if (this.body) {
        this.body.scrollTop = 0;
      }
    }

    close() {
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');

      document.body.classList.remove('th-lc-open');
    }

    createConfigurationId() {
      return (
        'TH-' +
        Date.now().toString(36).toUpperCase() +
        '-' +
        Math.random().toString(36).substring(2, 8).toUpperCase()
      );
    }

    /*
     * STEP 1: LENS TYPE
     */

    selectLensType(type) {
      this.configuration.lens.type = type;

      // Changing lens type clears the previous lens selection.
      this.configuration.lens.productId = null;
      this.configuration.lens.variantId = null;
      this.configuration.lens.title = null;
      this.configuration.lens.price = 0;

      this.root.querySelectorAll('[data-lens-type]').forEach(
        (button) => {
          button.classList.toggle(
            'is-selected',
            button.dataset.lensType === type
          );
        }
      );

      this.renderLensProducts();
      this.updateUI();
    }

    /*
     * STEP 2: LOAD LENS PRODUCTS
     */

    getLensProducts() {
      const dataElement = document.getElementById(
        'TAGHillsLensProductData'
      );

      if (!dataElement) {
        console.error(
          '[TAGHills] Lens product data element not found.'
        );
        return [];
      }

      try {
        const products = JSON.parse(dataElement.textContent);
        return Array.isArray(products) ? products : [];
      } catch (error) {
        console.error(
          '[TAGHills] Invalid lens product JSON.',
          error
        );
        return [];
      }
    }

    renderLensProducts() {
      if (!this.lensList) return;

      const selectedType = this.configuration.lens.type;

      if (!selectedType) {
        this.lensList.innerHTML = '';
        return;
      }

      const normalizedType = this.normalize(selectedType);

      const products = this.getLensProducts();

      const matchingProducts = products.filter((product) => {
        if (!product.lens_type) return false;

        const types = Array.isArray(product.lens_type)
          ? product.lens_type
          : [product.lens_type];

        return types.some(
          (type) => this.normalize(type) === normalizedType
        );
      });

      if (!matchingProducts.length) {
        this.lensList.innerHTML = `
          <div class="th-lc__empty">
            No lenses are available for this selection yet.
          </div>
        `;
        return;
      }

      this.lensList.innerHTML = matchingProducts
        .map((product) => this.createLensCard(product))
        .join('');
    }

    createLensCard(product) {
      const image = product.image
        ? `
          <div class="th-lc__lens-image">
            <img
              src="${this.escapeAttribute(product.image)}"
              alt="${this.escapeAttribute(product.title)}"
              loading="lazy"
            >
          </div>
        `
        : '<div class="th-lc__lens-image"></div>';

      const rawPrice = Number(product.price_raw);

      const price = Number.isFinite(rawPrice)
        ? this.formatMoney(rawPrice)
        : 'Price available at checkout';

      return `
        <button
          type="button"
          class="th-lc__lens-card"
          data-lens-product
          data-product-id="${this.escapeAttribute(product.id)}"
        >
          ${image}

          <span class="th-lc__lens-info">
            <span class="th-lc__lens-name">
              ${this.escapeHTML(product.title)}
            </span>

            <span class="th-lc__lens-price">
              ${this.escapeHTML(price)}
            </span>
          </span>

          <span class="th-lc__lens-radio"></span>
        </button>
      `;
    }

    selectLens(productId) {
      const products = this.getLensProducts();

      const product = products.find(
        (item) => String(item.id) === String(productId)
      );

      if (!product || !product.variant_id) {
        console.error(
          '[TAGHills] Selected lens or variant not found:',
          productId
        );

        alert('This lens is currently unavailable. Please select another.');
        return;
      }

      this.configuration.lens.productId = product.id;
      this.configuration.lens.variantId = String(product.variant_id);
      this.configuration.lens.title = product.title;
      this.configuration.lens.price = Number(product.price_raw || 0);

      this.root.querySelectorAll('[data-lens-product]').forEach(
        (card) => {
          card.classList.toggle(
            'is-selected',
            String(card.dataset.productId) === String(productId)
          );
        }
      );

      this.updateUI();
    }

    /*
     * STEP 3: PRESCRIPTION METHOD
     */

    selectPrescriptionMethod(method) {
      this.configuration.prescription.method = method;

      this.configuration.prescription.name = '';
      this.configuration.prescription.driveUrl = '';
      this.configuration.prescription.imageDataUrl = '';
      this.configuration.prescription.imageName = '';

      this.configuration.prescription.manual = {
        right: { sph: '', cyl: '', axis: '' },
        left: { sph: '', cyl: '', axis: '' },
        pd: ''
      };

      this.root.querySelectorAll('[data-prescription-method]').forEach(
        (button) => {
          button.classList.toggle(
            'is-selected',
            button.dataset.prescriptionMethod === method
          );
        }
      );

      this.renderPrescriptionDetails();
      this.updateUI();
    }

    renderPrescriptionDetails() {
      if (!this.prescriptionDetails) return;

      const method = this.configuration.prescription.method;

      if (!method || method === 'later') {
        this.prescriptionDetails.innerHTML = '';
        this.prescriptionDetails.hidden = true;
        return;
      }

      if (method === 'upload') {
        this.prescriptionDetails.innerHTML = `
          <div class="th-lc-prescription-form">
            <div class="th-lc-field">
              <label for="th-lc-prescription-name">
                Prescription Name
              </label>

              <input
                id="th-lc-prescription-name"
                type="text"
                data-prescription-name
                placeholder="e.g. Dad, Mom, My Prescription"
                autocomplete="off"
              >

              <small>
                Name this prescription so it can be matched to the
                correct frame and lens.
              </small>
            </div>

            <div class="th-lc-field">
              <label for="th-lc-prescription-image">
                Prescription Photo
              </label>

              <input
                id="th-lc-prescription-image"
                type="file"
                data-prescription-image
                accept="image/*,.heic,.heif"
              >

              <small>
                Take a clear photo or choose an existing image.
                JPG, PNG and other browser-supported image formats
                are accepted.
              </small>

              <div
                class="th-lc-upload-preview"
                data-prescription-image-preview
                hidden
              ></div>
            </div>
          </div>
        `;

        this.prescriptionDetails.hidden = false;
        return;
      }

      if (method === 'manual') {
        this.prescriptionDetails.innerHTML = `
          <div class="th-lc-prescription-form">
            <div class="th-lc-field">
              <label for="th-lc-manual-name">
                Prescription Name
              </label>

              <input
                id="th-lc-manual-name"
                type="text"
                data-prescription-name
                placeholder="e.g. Dad, Mom, My Prescription"
                autocomplete="off"
              >

              <small>
                This name keeps the prescription linked to the
                correct frame and lens configuration.
              </small>
            </div>

            <div class="th-lc-prescription-section">
              <div class="th-lc-prescription-section-title">
                Right Eye
              </div>

              <div class="th-lc-prescription-grid">
                <div class="th-lc-field">
                  <label for="th-lc-r-sph">SPH</label>
                  <input
                    id="th-lc-r-sph"
                    type="text"
                    inputmode="decimal"
                    data-r-sph
                    placeholder="SPH"
                  >
                </div>

                <div class="th-lc-field">
                  <label for="th-lc-r-cyl">CYL</label>
                  <input
                    id="th-lc-r-cyl"
                    type="text"
                    inputmode="decimal"
                    data-r-cyl
                    placeholder="CYL"
                  >
                </div>

                <div class="th-lc-field">
                  <label for="th-lc-r-axis">AXIS</label>
                  <input
                    id="th-lc-r-axis"
                    type="text"
                    inputmode="numeric"
                    data-r-axis
                    placeholder="AXIS"
                  >
                </div>
              </div>
            </div>

            <div class="th-lc-prescription-section">
              <div class="th-lc-prescription-section-title">
                Left Eye
              </div>

              <div class="th-lc-prescription-grid">
                <div class="th-lc-field">
                  <label for="th-lc-l-sph">SPH</label>
                  <input
                    id="th-lc-l-sph"
                    type="text"
                    inputmode="decimal"
                    data-l-sph
                    placeholder="SPH"
                  >
                </div>

                <div class="th-lc-field">
                  <label for="th-lc-l-cyl">CYL</label>
                  <input
                    id="th-lc-l-cyl"
                    type="text"
                    inputmode="decimal"
                    data-l-cyl
                    placeholder="CYL"
                  >
                </div>

                <div class="th-lc-field">
                  <label for="th-lc-l-axis">AXIS</label>
                  <input
                    id="th-lc-l-axis"
                    type="text"
                    inputmode="numeric"
                    data-l-axis
                    placeholder="AXIS"
                  >
                </div>
              </div>
            </div>

            <div class="th-lc-field">
              <label for="th-lc-pd">PD</label>

              <input
                id="th-lc-pd"
                type="text"
                inputmode="decimal"
                data-pd
                placeholder="e.g. 62"
              >

              <small>
                Enter your pupillary distance.
              </small>
            </div>
          </div>
        `;

        this.prescriptionDetails.hidden = false;
      }
    }

    /*
     * PHOTO HANDLING
     */

    async handlePrescriptionImage(file) {
      if (!file || !file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }

      if (file.size > 15 * 1024 * 1024) {
        alert('Please select an image smaller than 15 MB.');
        return;
      }

      try {
        const dataUrl = await this.compressPrescriptionImage(file);
        const base64 = dataUrl.split(',')[1] || '';

        if (!base64) {
          throw new Error('Could not process the selected image.');
        }

        const estimatedBytes = Math.floor(base64.length * 3 / 4);

        if (estimatedBytes > 3 * 1024 * 1024) {
          throw new Error(
            'The compressed photo is too large. Please choose another photo.'
          );
        }

        this.configuration.prescription.imageDataUrl = dataUrl;
        this.configuration.prescription.imageName =
          file.name || 'prescription.jpg';

        const preview = this.root.querySelector(
          '[data-prescription-image-preview]'
        );

        if (preview) {
          preview.replaceChildren();
          preview.hidden = false;

          const image = document.createElement('img');
          image.src = dataUrl;
          image.alt = 'Prescription photo preview';

          Object.assign(image.style, {
            display: 'block',
            width: '100%',
            maxHeight: '240px',
            objectFit: 'contain',
            borderRadius: '8px'
          });

          const message = document.createElement('p');
          message.textContent = 'Photo ready to upload';

          preview.append(image, message);
        }
      } catch (error) {
        console.error(
          '[TAGHills] Image processing failed:',
          error
        );

        alert(
          error.message ||
          'Could not process this photo. Please try a JPG or PNG.'
        );
      }
    }

    compressPrescriptionImage(file) {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
          URL.revokeObjectURL(objectUrl);

          const maxDimension = 1600;
          const largestDimension = Math.max(
            image.width,
            image.height
          );

          const scale = Math.min(
            1,
            maxDimension / largestDimension
          );

          const canvas = document.createElement('canvas');

          canvas.width = Math.max(
            1,
            Math.round(image.width * scale)
          );

          canvas.height = Math.max(
            1,
            Math.round(image.height * scale)
          );

          const context = canvas.getContext('2d');

          if (!context) {
            reject(
              new Error('Could not process the selected image.')
            );
            return;
          }

          context.drawImage(
            image,
            0,
            0,
            canvas.width,
            canvas.height
          );

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(
                  new Error('Could not convert the photo to JPEG.')
                );
                return;
              }

              const reader = new FileReader();

              reader.onload = () => resolve(reader.result);

              reader.onerror = () => reject(
                new Error('Could not read the converted photo.')
              );

              reader.readAsDataURL(blob);
            },
            'image/jpeg',
            0.8
          );
        };

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);

          reject(
            new Error(
              'This image format could not be opened. Please choose a JPG or PNG image.'
            )
          );
        };

        image.src = objectUrl;
      });
    }

    /*
     * CAPTURE FORM FIELDS
     */

    capturePrescriptionDetails() {
      const prescription = this.configuration.prescription;

      const getValue = (selector) => {
        const input = this.root.querySelector(selector);
        return input ? input.value.trim() : '';
      };

      prescription.name = getValue('[data-prescription-name]');

      if (prescription.method === 'upload') {
        // Keep imageDataUrl intact; the file handler sets it.
        return;
      }

      if (prescription.method === 'manual') {
        prescription.manual = {
          right: {
            sph: getValue('[data-r-sph]'),
            cyl: getValue('[data-r-cyl]'),
            axis: getValue('[data-r-axis]')
          },

          left: {
            sph: getValue('[data-l-sph]'),
            cyl: getValue('[data-l-cyl]'),
            axis: getValue('[data-l-axis]')
          },

          pd: getValue('[data-pd]')
        };
      }
    }

    validatePrescription() {
      this.capturePrescriptionDetails();

      const prescription = this.configuration.prescription;

      if (!prescription.method) {
        this.showValidationMessage(
          'Please select a prescription method.'
        );
        return false;
      }

      if (prescription.method === 'later') {
        return true;
      }

      if (!prescription.name) {
        this.showValidationMessage(
          'Please enter a prescription name.'
        );
        return false;
      }

      if (prescription.method === 'upload') {
        if (
          !prescription.imageDataUrl &&
          !prescription.driveUrl
        ) {
          this.showValidationMessage(
            'Please take or select a prescription photo.'
          );
          return false;
        }

        return true;
      }

      if (prescription.method === 'manual') {
        const right = prescription.manual.right;
        const left = prescription.manual.left;

        if (
          !right.sph ||
          !right.cyl ||
          !right.axis ||
          !left.sph ||
          !left.cyl ||
          !left.axis ||
          !prescription.manual.pd
        ) {
          this.showValidationMessage(
            'Please complete all prescription fields.'
          );
          return false;
        }

        return true;
      }

      return false;
    }

    showValidationMessage(message) {
      console.warn('[TAGHills]', message);
      alert(message);
    }

    /*
     * NAVIGATION
     */

    next() {
      if (this.isSubmitting) return;

      if (this.currentStep === 1) {
        if (!this.configuration.lens.type) {
          this.showValidationMessage(
            'Please select a lens type.'
          );
          return;
        }

        this.currentStep = 2;
        this.renderLensProducts();
      } else if (this.currentStep === 2) {
        if (!this.configuration.lens.variantId) {
          this.showValidationMessage(
            'Please select a lens.'
          );
          return;
        }

        this.currentStep = 3;
        this.renderPrescriptionDetails();
      } else if (this.currentStep === 3) {
        if (!this.validatePrescription()) return;

        this.finish();
        return;
      }

      this.updateUI();

      if (this.body) {
        this.body.scrollTop = 0;
      }
    }

    back() {
      if (this.currentStep <= 1 || this.isSubmitting) return;

      this.currentStep -= 1;
      this.updateUI();

      if (this.currentStep === 3) {
        this.renderPrescriptionDetails();
      }

      if (this.body) {
        this.body.scrollTop = 0;
      }
    }

    updateUI() {
      this.steps.forEach((stepElement) => {
        stepElement.classList.toggle(
          'is-active',
          Number(stepElement.dataset.step) === this.currentStep
        );
      });

      this.progress.forEach((line) => {
        line.classList.toggle(
          'is-active',
          Number(line.dataset.progress) <= this.currentStep
        );
      });

      if (this.backButton) {
        this.backButton.style.display =
          this.currentStep > 1 ? '' : 'none';
        this.backButton.disabled = this.isSubmitting;
      }

      if (this.nextButton) {
        let disabled = this.isSubmitting;

        if (this.currentStep === 1) {
          disabled ||= !this.configuration.lens.type;
        } else if (this.currentStep === 2) {
          disabled ||= !this.configuration.lens.variantId;
        } else if (this.currentStep === 3) {
          disabled ||= !this.configuration.prescription.method;
        }

        this.nextButton.disabled = disabled;

        if (!this.isSubmitting) {
          this.nextButton.textContent =
            this.currentStep === 3
              ? 'Add to Cart →'
              : 'Continue →';
        }
      }

      this.updateTotal();
    }

    updateTotal() {
      if (!this.totalElement) return;

      const total =
        Number(this.configuration.frame.price || 0) +
        Number(this.configuration.lens.price || 0);

      this.totalElement.textContent =
        total > 0 ? this.formatMoney(total) : '—';
    }

    formatMoney(amount) {
      const value = Number(amount);

      if (!Number.isFinite(value)) return '₹0';

      return (
        '₹' +
        (value / 100).toLocaleString('en-IN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        })
      );
    }

    clearSelections() {
      this.root.querySelectorAll('.is-selected').forEach(
        (element) => element.classList.remove('is-selected')
      );
    }

    /*
     * GOOGLE DRIVE PHOTO UPLOAD
     */

    async uploadPrescriptionImage() {
      const prescription = this.configuration.prescription;
      const dataUrl = prescription.imageDataUrl || '';
      const base64 = dataUrl.split(',')[1] || '';

      if (!this.configuration.id || !base64) {
        throw new Error(
          'Missing configuration ID or prescription photo.'
        );
      }

      if (!dataUrl.startsWith('data:image/jpeg;base64,')) {
        throw new Error(
          'The prescription photo must be converted to JPEG first.'
        );
      }

      const response = await fetch(TAGHILLS_PRESCRIPTION_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify({
          action: 'uploadPrescriptionImage',
          configurationId: this.configuration.id,
          fileName: 'prescription.jpg',
          mimeType: 'image/jpeg',
          base64: base64
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success || !result.driveUrl) {
        throw new Error(
          result.error ||
          'The prescription photo could not be uploaded.'
        );
      }

      prescription.driveUrl = result.driveUrl;

      return result.driveUrl;
    }

    /*
     * SAVE PRESCRIPTION TO GOOGLE SHEETS
     */

    async savePrescriptionToGoogleSheet() {
      const config = this.configuration;

      // Upload the photo first and store its Drive URL.
      if (
        config.prescription.method === 'upload' &&
        !config.prescription.driveUrl
      ) {
        await this.uploadPrescriptionImage();
      }

      const payload = {
        configurationId: config.id,

        frame: {
          productId: config.frame.productId,
          variantId: config.frame.variantId,
          title: config.frame.title,
          price: config.frame.price
        },

        lens: {
          type: config.lens.type,
          productId: config.lens.productId,
          variantId: config.lens.variantId,
          title: config.lens.title,
          price: config.lens.price
        },

        prescription: {
          method: config.prescription.method,
          name: config.prescription.name,
          driveUrl: config.prescription.driveUrl,
          manual: config.prescription.manual
        }
      };

      const response = await fetch(TAGHILLS_PRESCRIPTION_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.error || 'Unable to save prescription.'
        );
      }

      return result;
    }

    /*
     * FINISH: SAVE, THEN ADD FRAME + LENS TO CART
     */

    async finish() {
      if (this.isSubmitting) return;

      const config = this.configuration;

      if (!config.frame.productId || !config.frame.variantId) {
        this.showValidationMessage(
          'Missing frame data. Please close the configurator and select your frame again.'
        );
        return;
      }

      if (!config.lens.productId || !config.lens.variantId) {
        this.showValidationMessage(
          'Please select a valid lens.'
        );
        return;
      }

      if (!this.validatePrescription()) return;

      this.isSubmitting = true;
      this.updateUI();

      try {
        if (this.nextButton) {
          this.nextButton.textContent = 'Saving Prescription...';
        }

        // 1. Save prescription and image URL.
        await this.savePrescriptionToGoogleSheet();

        if (this.nextButton) {
          this.nextButton.textContent = 'Adding to Cart...';
        }

        // 2. Add both products with the same configuration ID.
        const rootUrl = window.Shopify?.routes?.root || '/';
        const cartUrl = rootUrl + 'cart/add.js';

        const frameProperties = {
          Lens: config.lens.title || '',
          'Lens Type': config.lens.type || '',
          Prescription: config.prescription.method || '',
          'Prescription Name': config.prescription.name || '',
          _configuration_id: config.id,
          _lens_variant_id: String(config.lens.variantId)
        };

        const lensProperties = {
          Frame: config.frame.title || '',
          Prescription: config.prescription.method || '',
          'Prescription Name': config.prescription.name || '',
          _configuration_id: config.id,
          _frame_variant_id: String(config.frame.variantId),
          _prescription_method: config.prescription.method || ''
        };

        const response = await fetch(cartUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(config.frame.variantId),
                quantity: 1,
                properties: frameProperties
              },
              {
                id: Number(config.lens.variantId),
                quantity: 1,
                properties: lensProperties
              }
            ]
          })
        });

        const cartData = await response.json();

        if (!response.ok) {
          throw new Error(
            cartData.description ||
            cartData.message ||
            'Unable to add frame and lens to cart.'
          );
        }

        this.close();

        window.location.href = rootUrl + 'cart';

      } catch (error) {
        console.error(
          '[TAGHills] Configuration submission failed:',
          error
        );

        alert(
          error.message ||
          'Something went wrong while saving your prescription.'
        );

        this.isSubmitting = false;
        this.updateUI();
      }
    }

    /*
     * HELPERS
     */

    normalize(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[\s\-\/]+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    }

    escapeHTML(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHTML(value);
    }
  }

  /*
   * INITIALIZE ONCE
   */

  function initializeLensConfigurator() {
    const root = document.getElementById(
      'TAGHillsLensConfigurator'
    );

    if (!root) {
      console.warn(
        '[TAGHills] No configurator found on this page.'
      );
      return;
    }

    // Avoid duplicate instances if the script runs more than once.
    if (
      window.TAGHillsLensConfigurator &&
      window.TAGHillsLensConfigurator.root === root
    ) {
      return;
    }

    const configurator = new TAGHillsLensConfigurator();

    if (!configurator.root) return;

    window.TAGHillsLensConfigurator = configurator;

    console.log(
      '[TAGHills] Configurator ready:',
      typeof configurator.open
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeLensConfigurator,
      { once: true }
    );
  } else {
    initializeLensConfigurator();
  }
})();